<template>
  <div>
    <!-- Loader -->
    <loading-panel :loading="loading"></loading-panel>

    <div class="row mb-2" v-if="activeTimer">
      <div class="col">
        <card class="active-timer-card" :class="{ 'paused': activeTimer.is_paused }">
          <div class="active-timer-content">
            <h1 class="active-timer-display text-white">
              <span v-if="activeTimer.is_paused" class="paused-indicator">⏸</span>
              {{ formatElapsedTimeWithColon(calculateRunningDurationInSeconds(activeTimer.start_time)) }}
            </h1>
            <p class="active-timer-subtitle">
              Started: {{ getLocalDateTime(activeTimer.start_time) }}
              <span v-if="activeTimer.is_paused" class="paused-text"> - PAUSED</span>
            </p>
            <p class="active-timer-project">
              Working on:
              <span v-if="activeTimer.project_name">
                <strong>{{ activeTimer.project_name }}</strong>
                <span v-if="activeTimer.ticket_key">, {{ activeTimer.ticket_key }} {{activeTimer.ticket_title}}</span>
              </span>
              <span v-else class="text-muted">No project</span>
            </p>
            <p class="active-timer-category" v-if="activeTimer.category_name">
              Category:
              <badge :style="{'background-color': activeTimer.category_color, 'padding': '4px 12px', 'font-size': '14px'}">
                {{ activeTimer.category_name }}
              </badge>
            </p>
            <p class="active-timer-notes" v-if="activeTimer.notes">
              {{ activeTimer.notes }}
            </p>
            <div class="active-timer-actions">
              <base-button
                type="primary"
                size="md"
                :disabled="togglingPause"
                @click="pauseTimer()"
              >
                <template v-if="togglingPause">
                  <i class="el-icon-loading mr-2"></i>
                  {{ pauseActionLabel }}
                </template>
                <template v-else>
                  <i :class="['tim-icons', 'mr-2', activeTimer.is_paused ? 'icon-triangle-right-17' : 'icon-button-pause']"></i>
                  {{ activeTimer.is_paused ? 'Resume' : 'Pause' }}
                </template>
              </base-button>
              <base-button type="primary" size="md" @click="openSwitchTaskModal()" v-if="hasPermissions('time_log_create')">
                <i class="tim-icons icon-refresh-02 mr-2"></i>
                Switch task
              </base-button>
              <base-button type="default" :disabled="stoppingTimer" @click="stopTimer()">
                <template v-if="stoppingTimer">
                  <i class="el-icon-loading mr-2"></i>
                  Stopping...
                </template>
                <template v-else>
                  <i class="tim-icons icon-simple-remove mr-2"></i>
                  Stop
                </template>
              </base-button>
            </div>
          </div>
        </card>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <card>
          <tab-rail :tabs="[{ name: 'Time tracker' }]" :value="0" tight class="mb-3">
            <template #actions>
              <base-button type="primary" v-if="!activeTimer && hasPermissions('time_log_create')" size="sm" @click="openStartTrackerModal()">
                <i class="tim-icons icon-triangle-right-17 mr-1"></i>
                Start tracking time
              </base-button>

              <el-dropdown v-if="hasPermissions('time_tracker_configure')">
                <base-button simple type="primary" link>
                  <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                </base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <a class="dropdown-item mt-2 mb-2" @click.prevent="configureCategories()" href="#">
                      <i class="tim-icons icon-components text-default mr-2"></i> Configure time tracking categories
                    </a>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
          </tab-rail>

          <filter-bar
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :loading="loading"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="time-logs"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
          >
            <template #actions>
              <base-button icon class="btn-link text-white" @click="downloadCSV">
                <i class="tim-icons icon-cloud-download-93"></i>
              </base-button>
            </template>
          </filter-bar>

          <!-- Analytics Dashboard -->
          <div class="row mb-4 mt-3" v-if="analytics">

            <!-- Filtered view: show total duration -->
            <template v-if="isDateRangeFilterActive">
              <div class="col-lg-2 col-md-6 col-12 mb-3">
                <card class="card-stats" :show-footer-line="true">
                  <div class="row stats-margin">
                    <div class="col-3">
                      <div class="info-icon icon-primary text-center">
                        <i class="tim-icons icon-watch-time"></i>
                      </div>
                    </div>
                    <div class="col-9">
                      <div class="numbers">
                        <p class="card-category">Total time</p>
                        <h4 class="card-title">{{ formatMinutesToTimeString(analytics.total_minutes) }}</h4>
                      </div>
                    </div>
                  </div>
                </card>
              </div>
            </template>

            <!-- Default view: today/week/month breakdown -->
            <template v-else>
              <div class="col-lg-2 col-md-6 col-12 mb-3">
                <card class="card-stats" :show-footer-line="true">
                  <div class="row stats-margin">
                    <div class="col-3">
                      <div class="info-icon icon-primary text-center">
                        <i class="tim-icons icon-calendar-60"></i>
                      </div>
                    </div>
                    <div class="col-9">
                      <div class="numbers">
                        <p class="card-category">Today</p>
                        <h4 class="card-title">{{ formatMinutesToTimeString(analytics.today_minutes) }}</h4>
                      </div>
                    </div>
                  </div>
                </card>
              </div>

              <div class="col-lg-2 col-md-6 col-12 mb-3">
                <card class="card-stats" :show-footer-line="true">
                  <div class="row stats-margin">
                    <div class="col-3">
                      <div class="info-icon icon-primary text-center">
                        <i class="tim-icons icon-chart-bar-32"></i>
                      </div>
                    </div>
                    <div class="col-9">
                      <div class="numbers">
                        <p class="card-category">This week</p>
                        <h4 class="card-title">{{ formatMinutesToTimeString(analytics.week_minutes) }}</h4>
                      </div>
                    </div>
                  </div>
                </card>
              </div>

              <div class="col-lg-2 col-md-6 col-12 mb-3">
                <card class="card-stats" :show-footer-line="true">
                  <div class="row stats-margin">
                    <div class="col-3">
                      <div class="info-icon icon-primary text-center">
                        <i class="tim-icons icon-chart-pie-36"></i>
                      </div>
                    </div>
                    <div class="col-9">
                      <div class="numbers">
                        <p class="card-category">This month</p>
                        <h4 class="card-title">{{ formatMinutesToTimeString(analytics.month_minutes) }}</h4>
                      </div>
                    </div>
                  </div>
                </card>
              </div>
            </template>

            <!-- Always shown -->
            <div class="col-lg-2 col-md-6 col-12 mb-3">
              <card class="card-stats" :show-footer-line="true">
                <div class="row stats-margin">
                  <div class="col-3">
                    <div class="info-icon icon-primary text-center">
                      <i class="tim-icons icon-single-02"></i>
                    </div>
                  </div>
                  <div class="col-9">
                    <div class="numbers">
                      <p class="card-category">Active users</p>
                      <h4 class="card-title">{{ analytics.total_users || 0 }}</h4>
                    </div>
                  </div>
                </div>
              </card>
            </div>

            <div class="col-lg-2 col-md-6 col-12 mb-3">
              <card class="card-stats" :show-footer-line="true">
                <div class="row stats-margin">
                  <div class="col-3">
                    <div class="info-icon icon-primary text-center">
                      <i class="tim-icons icon-app"></i>
                    </div>
                  </div>
                  <div class="col-9">
                    <div class="numbers">
                      <p class="card-category">Projects</p>
                      <h4 class="card-title">{{ analytics.total_projects || 0 }}</h4>
                    </div>
                  </div>
                </div>
              </card>
            </div>

            <div class="col-lg-2 col-md-6 col-12 mb-3">
              <card class="card-stats" :show-footer-line="true">
                <div class="row stats-margin">
                  <div class="col-3">
                    <div class="info-icon icon-primary text-center">
                      <i class="tim-icons icon-notes"></i>
                    </div>
                  </div>
                  <div class="col-9">
                    <div class="numbers">
                      <p class="card-category">Tickets</p>
                      <h4 class="card-title">{{ analytics.total_tickets || 0 }}</h4>
                    </div>
                  </div>
                </div>
              </card>
            </div>
          </div>

          <!-- No Time Logs Message -->
          <div v-if="timeLogs.length <= 0" class="mt-3">
            <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <h4 class="mt-3">No time logs found.</h4>
            </div>
          </div>

          <!-- Desktop Time Logs Table -->
          <div class="d-none d-sm-block" v-if="timeLogs.length > 0">
            <div class="row">
              <div class="col">
                <b-table striped hover :items="timeLogs" :fields="desktopFields">
                  <template v-slot:cell(start_time)="row">
                    {{ getLocalDateTime(row.item.start_time) }}
                  </template>

                  <template v-slot:cell(end_time)="row">
                    <span v-if="row.item.is_running" class="text-success">
                      <span class="running-pulse"></span>
                      <i class="tim-icons icon-triangle-right-17 ml-1"></i> Running
                    </span>
                    <span v-else-if="row.item.end_time">
                      {{ getLocalDateTime(row.item.end_time) }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>

                  <template v-slot:cell(duration)="row">
                    <span v-if="row.item.is_running" class="font-weight-bold">
                      <span v-if="row.item.is_paused" class="text-warning">
                        <i class="tim-icons icon-button-pause mr-1"></i>
                        {{ formatElapsedTime(calculateActiveMinutes(row.item)) }} (Paused)
                      </span>
                      <span v-else class="text-success">
                        {{ formatElapsedTime(calculateActiveMinutes(row.item)) }}
                      </span>
                    </span>
                    <span v-else>
                      {{ formatMinutesToTimeString(row.item.duration_minutes || 0) }}
                    </span>
                  </template>

                  <template v-slot:cell(project)="row">
                    <a v-if="row.item.project_id" :href="'/projects/' + row.item.project_id">
                      {{ row.item.project_name }}
                    </a>
                    <span v-else class="text-muted">No project</span>
                  </template>

                  <template v-slot:cell(ticket)="row">
                    <a v-if="row.item.ticket_id" :href="'/projects/' + row.item.project_id + '/tickets/' + row.item.ticket_id">
                      {{ row.item.ticket_key }}
                    </a>
                    <span v-else class="text-muted">-</span>
                  </template>

                  <template v-slot:cell(category)="row">
                    <badge v-if="row.item.category_name" :style="{'background-color': row.item.category_color}">
                      {{ row.item.category_name }}
                    </badge>
                    <span v-else class="text-muted">Uncategorized</span>
                    <el-tooltip v-if="locationChipLabel(row.item.start_location_check)" :content="locationTooltip(row.item)" placement="top">
                      <span class="tag-chip text-white ml-1" :style="tagChipStyle('#ff8d72')">{{ locationChipLabel(row.item.start_location_check) }}</span>
                    </el-tooltip>
                  </template>

                  <template v-slot:cell(user)="row">
                    {{ row.item.user_name }}
                  </template>

                  <template v-slot:cell(notes)="row">
                    <span v-if="row.item.notes" :title="row.item.notes">
                      {{ row.item.notes.substring(0, 30) }}{{ row.item.notes.length > 30 ? '...' : '' }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>

                  <template v-slot:cell(actions)="row">
                    <el-dropdown v-if="!row.item.is_running && (hasPermissions('time_log_update') || hasPermissions('time_log_delete'))">
                      <base-button
                        link
                        type="primary"
                        size="sm"
                      > <i style="font-size: large" class="tim-icons el-icon-more"></i>
                      </base-button>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <a class="dropdown-item mt-1 mb-2" v-if="hasPermissions('time_log_update')" @click.prevent="editTimeLogPrompt(row.item)" href="#">
                            <i class="tim-icons text-warning icon-pencil mr-2"></i> Update time log
                          </a>
                          <hr v-if="hasPermissions('time_log_delete')">
                          <a class="dropdown-item mt-1 mb-2" v-if="hasPermissions('time_log_delete')" @click.prevent="archiveTimeLogPrompt(row.item)" href="#">
                            <i class="tim-icons text-danger icon-trash-simple mr-2"></i> Delete time log
                          </a>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                    <span v-else class="text-muted">-</span>
                  </template>
                </b-table>
              </div>
            </div>
          </div>

          <!-- Mobile Time Logs View -->
          <div class="d-block d-sm-none" v-if="timeLogs.length > 0">
            <div class="row">
              <div class="col">
                <b-table striped hover :items="timeLogs" :fields="['time_log']">
                  <template #head(time_log)="row">
                    <span>Time log count: {{ totalRows }}</span>
                  </template>
                  <template v-slot:cell(time_log)="row">
                    <div class="row">
                      <div class="col">
                        <h5 class="mb-2">
                          <span v-if="row.item.is_running">
                            <span v-if="row.item.is_paused" class="text-warning">
                              <span class="running-pulse"></span>
                              <i class="tim-icons icon-button-pause mr-1"></i>
                              {{ formatElapsedTime(calculateActiveMinutes(row.item)) }} (Paused)
                            </span>
                            <span v-else class="text-success">
                              <span class="running-pulse"></span>
                              {{ formatElapsedTime(calculateActiveMinutes(row.item)) }}
                            </span>
                          </span>
                          <span v-else>
                            {{ formatMinutesToTimeString(row.item.duration_minutes || 0) }}
                          </span>
                        </h5>
                      </div>
                      <div class="col text-right">
                        <badge v-if="row.item.category_name" :style="{'background-color': row.item.category_color}">
                          {{ row.item.category_name }}
                        </badge>
                        <span v-if="locationChipLabel(row.item.start_location_check)" class="tag-chip text-white ml-1" :style="tagChipStyle('#ff8d72')">{{ locationChipLabel(row.item.start_location_check) }}</span>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <p class="mb-1">
                          <strong>{{ row.item.project_name || 'No project' }}</strong>
                          <span v-if="row.item.ticket_key"> - {{ row.item.ticket_key }}</span>
                        </p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <small>Started: {{ getLocalDateTime(row.item.start_time) }}</small>
                      </div>
                    </div>
                    <div class="row" v-if="row.item.end_time && !row.item.is_running">
                      <div class="col">
                        <small>Ended: {{ getLocalDateTime(row.item.end_time) }}</small>
                      </div>
                    </div>
                    <div class="row" v-if="row.item.notes">
                      <div class="col">
                        <small>{{ row.item.notes }}</small>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <small>User: {{ row.item.user_name }}</small>
                      </div>
                    </div>

                    <!-- Mobile Action Buttons -->
                    <div v-if="!row.item.is_running && (hasPermissions('time_log_update') || hasPermissions('time_log_delete'))"
                         class="btn-group btn-group-toggle mt-3 mb-2" data-toggle="buttons" style="width:100%">
                      <label class="btn btn-sm btn-primary btn-simple" v-if="hasPermissions('time_log_update')">
                        <base-button
                          @click="editTimeLogPrompt(row.item)"
                          type="primary"
                          class="btn btn-link"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-pencil"></i>
                        </base-button>
                      </label>
                      <label class="btn btn-sm btn-primary btn-simple" v-if="hasPermissions('time_log_delete')">
                        <base-button
                          @click="archiveTimeLogPrompt(row.item)"
                          type="primary"
                          class="btn btn-link"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-trash-simple"></i>
                        </base-button>
                      </label>
                    </div>
                  </template>
                </b-table>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @input="handlePageChange"
            v-if="timeLogs.length > 0"
          ></b-pagination>
        </card>
      </div>
    </div>

    <!-- Start New Tracker Modal -->
    <base-alert-dialog
      :show="alertDialog.show"
      :variant="alertDialog.variant"
      :title="alertDialog.title"
      :message="alertDialog.message"
      @confirm="alertDialog.show = false"
      @cancel="alertDialog.show = false"
    />

    <base-alert-dialog
      :show="confirmDialog.show"
      variant="warning"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      show-cancel
      :confirm-text="confirmDialog.confirmText"
      confirm-variant="danger"
      @confirm="resolveConfirm(true)"
      @cancel="resolveConfirm(false)"
    />

    <base-alert-dialog
      :show="locationWarning.show"
      variant="warning"
      :title="locationWarning.title"
      show-cancel
      confirm-text="Continue"
      @confirm="confirmLocationWarning"
      @cancel="locationWarning.show = false"
    >
      <p class="text-white">{{ locationWarning.message }}</p>
      <base-input label="Note" :error="locationWarning.error">
        <textarea class="form-control" rows="3" v-model="locationWarning.note" placeholder="Why are you starting off site?"></textarea>
      </base-input>
    </base-alert-dialog>

    <base-drawer :show="modals.start_tracker"
                 :title="isSwitchMode ? 'Switch to a new task' : 'Start new time recording'"
                 @close="closeStartTrackerModal()">
      <template #header-extra>
        <div v-if="isSwitchMode && activeTimer" class="switch-context-banner mt-3">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <small class="text-white" style="opacity: 0.7">Stopping current task</small>
              <p class="mb-0 text-white">
                <strong>{{ activeTimer.project_name || 'No project' }}</strong>
                <span v-if="activeTimer.ticket_key"> &mdash; {{ activeTimer.ticket_key }} {{ activeTimer.ticket_title }}</span>
              </p>
            </div>
            <div class="text-right">
              <span class="switch-elapsed-time">
                {{ formatElapsedTimeWithColon(calculateRunningDurationInSeconds(activeTimer.start_time)) }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <drawer-section title="What to track" icon="icon-tag">
        <base-input label="Category" placeholder="Select a category" :error="errs.category_id" required>
          <el-select v-model="newTracker.category_id" class="select-primary"
                     placeholder="Select category"
                     filterable
                     clearable
                     style="width: 100%"
                     :class="{ 'is-invalid': errs.category_id }">
            <el-option
              v-for="category in categories.filter(c => c.archived === 0)"
              :key="category.id"
              :label="category.name"
              :value="category.id">
              <div style="display: flex; align-items: center;">
            <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; margin-right: 8px;"
                  :style="{'background-color': category.label_color || category.color}"></span>
                {{ category.name }}
              </div>
            </el-option>
          </el-select>
        </base-input>

        <base-input label="Project" placeholder="Select a project">
          <el-select v-model="newTracker.project_id"
                     placeholder="Select project"
                     class="select-primary"
                     filterable
                     clearable
                     @change="onProjectChange"
                     style="width: 100%">
            <el-option
              v-for="project in projects"
              :key="project.id"
              :label="project.name"
              :value="project.id">
            </el-option>
          </el-select>
        </base-input>

        <base-input label="Ticket" placeholder="Select a ticket">
          <el-select v-model="newTracker.ticket_id"
                     placeholder="Select ticket"
                     class="select-primary"
                     filterable
                     clearable
                     :disabled="!newTracker.project_id"
                     @change="onTicketChange"
                     style="width: 100%">
            <el-option
              v-for="ticket in projectTickets"
              :key="ticket.id"
              :label="`${ticket.key} ${ticket.title}`"
              :value="ticket.id">
            </el-option>
          </el-select>
        </base-input>

        <div v-if="selectedTicketHasSubtasks">
          <base-input label="Subtask" placeholder="Select a subtask">
            <el-select v-model="newTracker.subtask_id"
                       placeholder="Whole ticket (no subtask)"
                       class="select-primary"
                       filterable
                       clearable
                       style="width: 100%">
              <el-option
                v-for="subtask in projectSubtasks"
                :key="subtask.id"
                :label="`${subtask.key} ${subtask.title}`"
                :value="subtask.id">
              </el-option>
            </el-select>
          </base-input>
        </div>
      </drawer-section>

      <drawer-section v-if="!isSwitchMode" title="Start time" icon="icon-time-alarm">
        <div class="form-group">
          <el-switch active-color="#e14eca" v-model="newTracker.useCustomTime" />
          <label class="text-white ml-2">Set custom start time</label>
        </div>
        <div v-if="newTracker.useCustomTime">
          <base-input label="Start Date" placeholder="Select date">
            <el-date-picker
              v-model="newTracker.start_date"
              type="date"
              placeholder="Select date"
              :format="day_of_week + '' + dateFormatCal"
              value-format="yyyy-MM-dd"
              style="width: 100%">
            </el-date-picker>
          </base-input>

          <base-input label="Start Time" placeholder="Select time">
            <el-time-picker
              v-model="newTracker.start_time_only"
              placeholder="Select time"
              :format="picker_time_format"
              value-format="HH:mm:ss"
              style="width: 100%">
            </el-time-picker>
          </base-input>
        </div>
      </drawer-section>

      <drawer-section title="Notes" icon="icon-notes">
        <base-input label="Notes" placeholder="Add notes (optional)">
          <textarea class="form-control"
                    v-model="newTracker.notes"
                    rows="3"
                    placeholder="Enter any notes about this time entry..."></textarea>
        </base-input>
      </drawer-section>

      <template #footer>
        <div class="footer-actions">
          <base-button type="secondary" size="sm" @click="closeStartTrackerModal()">Cancel</base-button>
          <base-button type="primary" size="sm" :loading="submittingTracker" @click="isSwitchMode ? switchTask() : startTracker()">
            {{ isSwitchMode ? 'Switch task' : 'Start tracking' }}
          </base-button>
        </div>
      </template>
    </base-drawer>

    <!-- Update Time Log Modal -->
    <modal :show.sync="modals.edit_time_log"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <div class="row">
          <div class="col">
            <h4 class="card-title">Update time log</h4>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <base-input label="Category" placeholder="Select a category" :error="errs.edit_category_id" required>
              <el-select v-model="editTracker.category_id"
                         placeholder="Select category"
                         class="select-primary"
                         filterable
                         clearable
                         style="width: 100%"
                         :class="{ 'is-invalid': errs.edit_category_id }">
                <el-option
                  v-for="category in categories.filter(c => c.archived === 0)"
                  :key="category.id"
                  :label="category.name"
                  :value="category.id">
                  <div style="display: flex; align-items: center;">
                <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; margin-right: 8px;"
                      :style="{'background-color': category.label_color || category.color}"></span>
                    {{ category.name }}
                  </div>
                </el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <base-input label="Project" placeholder="Select a project">
              <el-select v-model="editTracker.project_id"
                         placeholder="Select project"
                         class="select-primary"
                         filterable
                         clearable
                         @change="onEditProjectChange"
                         style="width: 100%">
                <el-option
                  v-for="project in projects"
                  :key="project.id"
                  :label="project.name"
                  :value="project.id">
                </el-option>
              </el-select>
            </base-input>
          </div>
          <div class="col-lg-6">
            <base-input label="Ticket" placeholder="Select a ticket">
              <el-select v-model="editTracker.ticket_id"
                         placeholder="Select ticket"
                         class="select-primary"
                         filterable
                         clearable
                         :disabled="!editTracker.project_id"
                         @change="onEditTicketChange"
                         style="width: 100%">
                <el-option
                  v-for="ticket in editProjectTickets"
                  :key="ticket.id"
                  :label="`${ticket.key} ${ticket.title}`"
                  :value="ticket.id">
                </el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <div class="row" v-if="selectedEditTicketHasSubtasks">
          <div class="col-lg-6 offset-lg-6">
            <base-input label="Subtask" placeholder="Select a subtask">
              <el-select v-model="editTracker.subtask_id"
                         placeholder="Whole ticket (no subtask)"
                         class="select-primary"
                         filterable
                         clearable
                         style="width: 100%">
                <el-option
                  v-for="subtask in editProjectSubtasks"
                  :key="subtask.id"
                  :label="`${subtask.key} ${subtask.title}`"
                  :value="subtask.id">
                </el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <!-- Start Time Section -->
        <div class="row">
          <div class="col">
            <h6 class="mb-3">Start Time</h6>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6">
            <base-input label="Start Date" placeholder="Select start date">
              <el-date-picker
                v-model="editTracker.start_date"
                type="date"
                placeholder="Select date"
                :format="day_of_week + '' + dateFormatCal"
                value-format="yyyy-MM-dd"
                style="width: 100%">
              </el-date-picker>
            </base-input>
          </div>
          <div class="col-lg-6">
            <base-input label="Start Time" placeholder="Select start time">
              <el-time-picker
                v-model="editTracker.start_time_only"
                placeholder="Select time"
                :format="picker_time_format"
                value-format="HH:mm:ss"
                style="width: 100%">
              </el-time-picker>
            </base-input>
          </div>
        </div>

        <!-- End Time Section -->
        <div class="row">
          <div class="col">
            <h6 class="mb-3">End Time</h6>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6">
            <base-input label="End Date" placeholder="Select end date">
              <el-date-picker
                v-model="editTracker.end_date"
                type="date"
                placeholder="Select date"
                :format="day_of_week + '' + dateFormatCal"
                value-format="yyyy-MM-dd"
                style="width: 100%">
              </el-date-picker>
            </base-input>
          </div>
          <div class="col-lg-6">
            <base-input label="End Time" placeholder="Select end time">
              <el-time-picker
                v-model="editTracker.end_time_only"
                placeholder="Select time"
                :format="picker_time_format"
                value-format="HH:mm:ss"
                style="width: 100%">
              </el-time-picker>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input label="Notes" placeholder="Add notes (optional)">
          <textarea class="form-control"
                    v-model="editTracker.notes"
                    rows="3"
                    placeholder="Enter any notes about this time entry..."></textarea>
            </base-input>
          </div>
        </div>

        <div class="row text-center mt-3">
          <div class="col">
            <base-button type="secondary" @click="closeEditTimeLogModal()">Cancel</base-button>
            <base-button class="ml-2" type="primary" @click="updateTimeLog()">
              Save
            </base-button>
          </div>
        </div>

      </card>
    </modal>

    <!-- Configure categories drawer -->
    <base-drawer :show="modals.configure_categories" :title="categoryDrawerTitle" @close="closeCategoriesDrawer()">
      <template v-if="edit_categories" #title-prefix>
        <button type="button" class="drawer-back-btn" aria-label="Back to categories" @click="edit_categories = false">
          <i class="tim-icons icon-minimal-left"></i>
        </button>
      </template>
      <category-list-view v-if="!edit_categories"
                          :categories="categories"
                          @add="addNewCategory()"
                          @edit="editCategory" />
      <category-edit-view v-else
                          ref="categoryEdit"
                          :key="selectedCategory ? selectedCategory.id : 'new'"
                          :category="selectedCategory"
                          :company-id="companyId"
                          :company-group-id="companyGroupId"
                          :venue-pin-set="venuePinSet"
                          :off-site-automation-active="offSiteAutomationActive"
                          :server-errors="categoryErrors"
                          @save="saveCategory" />
      <div v-if="edit_categories && selectedCategory" class="danger-action-card">
        <div class="danger-action-info">
          <span class="danger-action-label text-white">Archive category</span>
          <span class="danger-action-hint text-white opacity-7">Remove it from the category list. Existing time logs keep it.</span>
        </div>
        <base-button size="sm" class="danger-action-btn" :disabled="savingCategory" @click="archiveCategory(selectedCategory)">
          <i class="tim-icons icon-trash-simple mr-1" style="font-size: 11px;"></i>
          Archive
        </base-button>
      </div>
      <template #footer>
        <div class="footer-actions">
          <template v-if="edit_categories">
            <base-button type="secondary" size="sm" @click="edit_categories = false">Cancel</base-button>
            <base-button type="primary" size="sm" :loading="savingCategory" @click="$refs.categoryEdit.submit()">Save category</base-button>
          </template>
          <base-button v-else type="secondary" size="sm" @click="closeCategoriesDrawer()">Close</base-button>
        </div>
      </template>
    </base-drawer>
  </div>
</template>

<script>
import { BPagination, BTable } from "bootstrap-vue";
import { Badge } from "@/components";
import moment from "moment-timezone";
import { formatGroupDate, formatGroupTime } from "@/plugins/dates";
import { Auth } from "aws-amplify";
import { API_LOCATION } from "@/api-config";
import TabRail from '@/components/TabRail';
import CategoryListView from './CategoryListView.vue';
import CategoryEditView from './CategoryEditView.vue';
import { getDeviceLocation, describeLocationCheck, locationChipLabel, locationResultLabel, formatDistance, groupCountry } from '@/util/locationCheck';
import { tagChipStyle } from '@/util/tagChip';
import axios from "axios";
import {
  Dropdown,
  DropdownMenu,
  Select,
  Option,
  DatePicker,
  Switch,
  TimePicker
} from "element-ui";

export default {
  name: "TimeLogsList",
  components: {
    TabRail,
    CategoryListView,
    CategoryEditView,
    BTable,
    BPagination,
    Badge,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Switch.name]: Switch,
    [TimePicker.name]: TimePicker,
  },
  data() {
    return {
      loading: false,
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      picker_time_format: 'hh:mm A',
      day_of_week: '',
      // Date format for calendar
      dateFormatCal: '',

      // Data
      timeLogs: [],
      analytics: null,
      categories: [],
      projects: [],
      projectTickets: [],
      editProjectTickets: [],
      projectSubtasks: [],
      editProjectSubtasks: [],
      users: [],

      // Pagination
      currentPage: 1,
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      totalRows: 0,

      // Flat filter values for the shared <filter-bar>. Seeded from the URL;
      // saved-views precedence is resolved by FilterBar.
      filterValues: this.buildInitialFilterValues(),
      didInitialFetch: false,

      // Resolved filter state read by buildQueryParams / updateUrlQuery
      filters: {
        project: [],
        category: [],
        user: [],
        date_range: { from: '', to: '' },
        running_status: ''
      },

      alertDialog: { show: false, title: '', message: '', variant: 'info' },
      locationWarning: { show: false, title: '', message: '', note: '', error: '' },
      confirmDialog: { show: false, title: '', message: '', confirmText: 'Confirm', resolve: null },

      // Table configuration
      desktopFields: [
        { key: 'start_time', label: 'Start Time' },
        { key: 'end_time', label: 'End Time' },
        { key: 'duration', label: 'Duration' },
        { key: 'project', label: 'Project' },
        { key: 'ticket', label: 'Ticket' },
        { key: 'category', label: 'Category' },
        { key: 'user', label: 'User' },
        { key: 'notes', label: 'Notes' },
        { key: 'actions', label: 'Actions' }
      ],
      modals: {
        configure_categories: false,
        start_tracker: false,
        edit_time_log: false
      },
      activeTimer: null,
      venuePinSet: false,
      locationChecksEnabled: false,
      offSiteAutomationActive: false,
      isSwitchMode: false,
      submittingTracker: false,
      togglingPause: false,
      pauseActionLabel: '',
      stoppingTimer: false,
      // Timer for running logs
      timerInterval: null,

      // New tracker data
      newTracker: {
        project_id: null,
        ticket_id: null,
        subtask_id: null,
        category_id: null,
        useCustomTime: false,
        start_date: null,
        start_time_only: null,
        notes: ''
      },

      // Edit tracker data
      editTracker: {
        id: null,
        project_id: null,
        ticket_id: null,
        subtask_id: null,
        category_id: null,
        start_date: null,
        start_time_only: null,
        end_date: null,
        end_time_only: null,
        duration_minutes: null,
        notes: ''
      },

      // Category editing
      edit_categories: false,
      selectedCategory: null,
      categoryErrors: {},
      savingCategory: false,
      errs: {
        category_id: '',
        edit_category_id: ''
      }
    }
  },
  computed: {
    categoryDrawerTitle() {
      if (!this.edit_categories) return 'Configure time tracking categories'
      return this.selectedCategory ? 'Edit category' : 'New category'
    },
    filterSchema() {
      return [
        {key: 'project', type: 'multiselect', label: 'Project', primary: true,
          options: this.projects.map(p => ({label: p.name, value: p.id}))},
        {key: 'category', type: 'multiselect', label: 'Category', primary: true,
          options: this.categories.filter(c => c.archived === 0).map(c => ({label: c.name, value: c.id}))},
        {key: 'user', type: 'multiselect', label: 'User', primary: true,
          options: this.users.map(u => ({label: u.name, value: u.id}))},
        {key: 'date_range', type: 'daterange', label: 'Date range', primary: true},
        {key: 'running_status', type: 'select', label: 'Status', primary: true,
          options: [{label: 'Running', value: 'true'}, {label: 'Completed', value: 'false'}]},
      ]
    },
    selectedTicketHasSubtasks() {
      const t = this.projectTickets.find(t => t.id === this.newTracker.ticket_id)
      return !!(t && Number(t.subtask_count) > 0)
    },
    selectedEditTicketHasSubtasks() {
      const t = this.editProjectTickets.find(t => t.id === this.editTracker.ticket_id)
      return !!(t && Number(t.subtask_count) > 0)
    },
    isDateRangeFilterActive() {
      return this.filters.date_range.from !== '' ||
        this.filters.date_range.to !== ''
    }
  },
  watch: {
    'modals.start_tracker'(open) {
      if (open) {
        document.addEventListener('keydown', this._onStartDrawerEsc)
      } else {
        document.removeEventListener('keydown', this._onStartDrawerEsc)
      }
    },
    'modals.configure_categories'(open) {
      if (open) {
        document.addEventListener('keydown', this._onCategoriesDrawerEsc)
      } else {
        document.removeEventListener('keydown', this._onCategoriesDrawerEsc)
      }
    },
  },
  created() {
    // Initialize from query parameters
    this.initializeFromQuery()
    this._onStartDrawerEsc = (e) => {
      if (e.key === 'Escape' && this.modals.start_tracker) {
        this.closeStartTrackerModal()
      }
    }
    this._onCategoriesDrawerEsc = (e) => {
      if (e.key === 'Escape' && this.modals.configure_categories) {
        // The edit view goes back to the list; the list closes the drawer.
        if (this.edit_categories) this.edit_categories = false
        else this.closeCategoriesDrawer()
      }
    }
  },
  mounted() {
    // Set up date format for calendar
    this.dateFormatCal = this.date_format.replace(/Y/g, 'y').replace(/D/g, 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }
    if (this.time_format === 'HH:mm') {
      this.picker_time_format = 'HH:mm'
    }
    this.perPage = this.$route.query.count ? Number(this.$route.query.count) : 10;
    this.currentPage = this.$route.query.page ? Number(this.$route.query.page) : 1;
    // FilterBar (saved-filters-key) owns the first fetch and emits its initial @change on mount.
  },
  beforeDestroy() {
    this.stopTimerUpdate();
    document.removeEventListener('keydown', this._onStartDrawerEsc);
    document.removeEventListener('keydown', this._onCategoriesDrawerEsc);
  },
  methods: {
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    async downloadCSV() {
      try {
        this.loading = true;
        const allTimeLogs = await this.getAllTimeLogs();

        const headers = [
          'Start Time',
          'End Time',
          'Duration (minutes)',
          'Project',
          'Ticket',
          'Category',
          'User',
          'Notes',
          'Status',
          'Location check'
        ];

        const csvData = allTimeLogs.map(log => [
          this.getLocalDateTime(log.start_time),
          log.end_time ? this.getLocalDateTime(log.end_time) : (log.is_running ? 'Running' : '-'),
          log.is_running ? this.calculateActiveMinutes(log) : (log.duration_minutes || 0),
          log.project_name || 'No project',
          log.ticket_key || '-',
          log.category_name || 'Uncategorized',
          log.user_name,
          log.notes || '-',
          log.is_running ? (log.is_paused ? 'Paused' : 'Running') : 'Completed',
          locationResultLabel(log.start_location_check) || '-'
        ]);

        this.downloadCSVFile(csvData, headers, 'time-logs.csv');
      } catch (error) {
        console.error('Error exporting time logs:', error);
        this.showAlert('Error', 'Failed to export time logs', 'danger');
      } finally {
        this.loading = false;
      }
    },

    async getAllTimeLogs() {
      const token = await this.getAuthToken();
      const config = { headers: { Authorization: token } };
      const allTimeLogs = [];
      let offset = 0;
      const count = 1000;

      while (offset <= this.totalRows) {
        const params = new URLSearchParams(this.buildQueryParams());
        params.set('count', count);
        params.set('offset', offset);

        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs?${params.toString()}`;
        const response = await axios.get(url, config);

        allTimeLogs.push(...(response.data.time_logs || []));
        offset += count;

        // Break if we've gotten all the data
        if (response.data.time_logs.length < count) {
          break;
        }
      }

      return allTimeLogs;
    },

// CSV download utility
    downloadCSVFile(data, headers, filename) {
      let csv = headers.join(',') + '\n';

      data.forEach(row => {
        csv += row.map(value => {
          if (typeof value === 'string') {
            // Escape quotes and wrap in quotes if contains comma or newline
            value = value.replace(/"/g, '""');
            if (value.includes(',') || value.includes('\n')) {
              value = `"${value}"`;
            }
          }
          return value || '';
        }).join(',') + '\n';
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    // Initialize from URL query parameters
    initializeFromQuery() {
      const query = this.$route.query

      this.perPage = Number(query.count) || 10
      this.currentPage = Number(query.page) || 1
    },

    buildInitialFilterValues() {
      const q = this.$route.query || {}
      const ids = v => v ? String(v).split(',').map(Number).filter(n => !isNaN(n)) : []
      return {
        project: ids(q.project),
        category: ids(q.category),
        user: ids(q.user),
        date_range: {mode: 'range', days: '', from: q.date_range_from || '', to: q.date_range_to || ''},
        running_status: q.running_status || '',
      }
    },

    onFiltersChanged(values) {
      const c = values.date_range || {}
      let from = '', to = ''
      if (c.mode === 'days' && c.days) {
        const today = new Date()
        const dt = new Date()
        dt.setDate(dt.getDate() - Number(c.days))
        const fmt = d => d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2)
        from = fmt(dt)
        to = fmt(today)
      } else if (c.mode === 'range' && c.from && c.to) {
        from = c.from
        to = c.to
      }
      this.filters = {
        project: Array.isArray(values.project) ? values.project : [],
        category: Array.isArray(values.category) ? values.category : [],
        user: Array.isArray(values.user) ? values.user : [],
        date_range: { from, to },
        running_status: values.running_status || ''
      }
      this.filterValues = {...this.filterValues, ...values, date_range: {...c}}
      if (!this.didInitialFetch) {
        // Preserve the URL page on the first fetch.
        this.didInitialFetch = true
        this.getTimeLogs(this.perPage, this.getOffset())
      } else {
        this.currentPage = 1
        this.getTimeLogs(this.perPage, this.getOffset())
      }
    },

    onPerPageChange(n) {
      this.perPage = n
      this.currentPage = 1
      this.getTimeLogs(this.perPage, this.getOffset())
    },

    notifyCategory(type, message) {
      this.$notify({ type, message, icon: type === 'success' ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc' })
    },

    showAlert(title, message, variant) {
      this.alertDialog = { show: true, title, message, variant }
    },

    tagChipStyle,
    locationChipLabel,

    locationTooltip(log) {
      if (log.start_location_check === 'outside') return formatDistance(log.start_distance_m, groupCountry()) + ' from the venue at start'
      if (log.start_location_check === 'unavailable') return 'Location could not be confirmed at start'
      return 'Added or changed manually'
    },

    parseErrorBody(err) {
      const data = err && err.response ? err.response.data : null
      if (data && typeof data === 'object') return data
      try { return JSON.parse(data) } catch (e) { return null }
    },

    // Location is only requested when a category in this group uses location checks.
    async locationPayload() {
      if (!this.locationChecksEnabled) return {}
      const { location, location_error } = await getDeviceLocation()
      return { location, location_error }
    },

    // Shows the warning note dialog or the blocked alert for a location check
    // response. Returns true when the error was a location check.
    handleLocationCheckError(err) {
      const body = this.parseErrorBody(err)
      if (!body || !body.code) return false
      if (body.code === 'location_note_required') {
        const copy = describeLocationCheck(body)
        this.locationWarning = { show: true, title: copy.title, message: copy.message, note: this.newTracker.notes || '', error: '' }
        return true
      }
      if (body.code === 'location_blocked') {
        const copy = describeLocationCheck(body)
        this.showAlert(copy.title, copy.message, 'danger')
        return true
      }
      if (body.code === 'location_manual_not_allowed') {
        this.showAlert('Not allowed', body.error, 'danger')
        return true
      }
      return false
    },

    async confirmLocationWarning() {
      if (!this.locationWarning.note || !this.locationWarning.note.trim()) {
        this.locationWarning.error = 'Add a note to continue'
        return
      }
      this.newTracker.notes = this.locationWarning.note.trim()
      this.locationWarning.show = false
      await this.$nextTick()
      await this.startTracker({ location_warning_acknowledged: true })
    },

    askConfirm(title, message, confirmText) {
      return new Promise(resolve => {
        this.confirmDialog = { show: true, title, message, confirmText, resolve }
      })
    },

    resolveConfirm(value) {
      const resolve = this.confirmDialog.resolve
      this.confirmDialog = { show: false, title: '', message: '', confirmText: 'Confirm', resolve: null }
      if (resolve) resolve(value)
    },

    // Time Log Actions
    async editTimeLogPrompt(timeLog) {
      const tz = JSON.parse(localStorage.getItem('group')).tz_name
      const toWallClock = (utc) => tz ? moment.utc(utc).tz(tz) : moment(utc)

      const startMoment = toWallClock(timeLog.start_time);
      const startDate = startMoment.format('YYYY-MM-DD');
      const startTime = startMoment.format('HH:mm:ss');

      let endDate = null;
      let endTime = null;
      if (timeLog.end_time) {
        const endMoment = toWallClock(timeLog.end_time);
        endDate = endMoment.format('YYYY-MM-DD');
        endTime = endMoment.format('HH:mm:ss');
      }

      const parentId = timeLog.ticket_parent_id || null;
      this.editTracker = {
        id: timeLog.id,
        project_id: timeLog.project_id,
        ticket_id: parentId ? parentId : timeLog.ticket_id,
        subtask_id: parentId ? timeLog.ticket_id : null,
        category_id: timeLog.category_id,
        start_date: startDate,
        start_time_only: startTime,
        end_date: endDate,
        end_time_only: endTime,
        duration_minutes: timeLog.duration_minutes || 0,
        notes: timeLog.notes || ''
      };

      // Load tickets for the selected project without clearing the log's own selection
      this.editProjectSubtasks = [];
      if (timeLog.project_id) {
        await this.loadEditProjectTickets(timeLog.project_id);
        if (this.editTracker.ticket_id && this.selectedEditTicketHasSubtasks) {
          this.editProjectSubtasks = await this.loadSubtasksForTicket(timeLog.project_id, this.editTracker.ticket_id);
        }
      }

      this.modals.edit_time_log = true;
    },
    closeEditTimeLogModal() {
      this.modals.edit_time_log = false;
      this.editTracker = {
        id: null,
        project_id: null,
        ticket_id: null,
        subtask_id: null,
        category_id: null,
        start_time: null,
        end_time: null,
        duration_minutes: null,
        notes: ''
      };
      this.editProjectTickets = [];
      this.editProjectSubtasks = [];
      this.errs.edit_category_id = '';
    },
    validateEditTimeLog() {
      // Reset errors
      this.errs.edit_category_id = '';

      let isValid = true;

      // Validate category
      if (!this.editTracker.category_id) {
        this.errs.edit_category_id = 'Please select a category';
        isValid = false;
      }

      return isValid;
    },
    async loadEditProjectTickets(projectId) {
      this.editProjectTickets = [];
      if (!projectId) return;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${projectId}/tickets?count=10000&offset=0`;
        const response = await axios.get(url, config);
        this.editProjectTickets = response.data.tickets || [];
      } catch (err) {
        console.error('Failed to load tickets:', err);
      }
    },
    async onEditProjectChange(projectId) {
      // Clear ticket selection when project changes
      this.editTracker.ticket_id = null;
      this.editTracker.subtask_id = null;
      this.editProjectSubtasks = [];
      await this.loadEditProjectTickets(projectId);
    },

    async updateTimeLog() {
      // Validate form before submission
      if (!this.validateEditTimeLog()) {
        return;
      }

      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };

        const tz = JSON.parse(localStorage.getItem('group')).tz_name
        const toUtcIso = (date, t) => {
          const local = `${date} ${t}`
          return (tz ? moment.tz(local, tz) : moment(local)).utc().toISOString()
        }

        let startTime = null;
        if (this.editTracker.start_date && this.editTracker.start_time_only) {
          startTime = toUtcIso(this.editTracker.start_date, this.editTracker.start_time_only)
        }

        let endTime = null;
        if (this.editTracker.end_date && this.editTracker.end_time_only) {
          endTime = toUtcIso(this.editTracker.end_date, this.editTracker.end_time_only)
        }

        const data = {
          project_id: this.editTracker.project_id,
          ticket_id: this.editTracker.subtask_id || this.editTracker.ticket_id,
          category_id: this.editTracker.category_id,
          start_time: startTime,
          end_time: endTime,
          notes: this.editTracker.notes || null
        };

        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs/${this.editTracker.id}`;
        await axios.put(url, data, config);

        // Close modal and refresh the list
        this.closeEditTimeLogModal();
        await this.getTimeLogs(this.perPage, this.getOffset());

        this.showAlert('Success', 'Time log updated successfully', 'success');

      } catch (err) {
        this.loading = false;
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else if (err.response?.status === 403 && this.handleLocationCheckError(err)) {
          // Shown by handleLocationCheckError
        } else if (err.response?.status === 400) {
          this.showAlert('Error', err.response.data, 'danger');
        } else {
          this.showAlert('Error', 'Failed to update time log', 'danger');
        }
      }
    },

    async archiveTimeLogPrompt(timeLog) {
      const ok = await this.askConfirm('Delete this time log?', 'The time log will be permanently deleted.', 'Delete');
      if (ok) this.archiveTimeLog(timeLog);
    },

    async archiveTimeLog(timeLog) {
      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs/${timeLog.id}`;

        await axios.delete(url, config);

        // Refresh the list
        await this.getTimeLogs(this.perPage, this.getOffset());

        this.showAlert('Success', 'Time log archived successfully', 'success');

      } catch (err) {
        this.loading = false;
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else {
          this.showAlert('Error', 'Failed to archive time log', 'danger');
        }
      }
    },

    // Filter management
    // Build query parameters for API
    buildQueryParams() {
      const params = new URLSearchParams()

      params.append('count', this.perPage)
      params.append('offset', this.getOffset())

      // Add filters
      if (this.filters.project.length > 0) params.append('project_id', this.filters.project.join(','))
      if (this.filters.category.length > 0) params.append('category_id', this.filters.category.join(','))
      if (this.filters.user.length > 0) params.append('user_id', this.filters.user.join(','))
      if (this.filters.date_range.from) params.append('start_date', this.filters.date_range.from)
      if (this.filters.date_range.to) params.append('end_date', this.filters.date_range.to)
      if (this.filters.running_status) params.append('is_running', this.filters.running_status)

      return params.toString()
    },

    // URL management
    updateUrlQuery() {
      const query = {
        count: this.perPage,
        page: this.currentPage
      }

      // Preserve onboarding params
      if (this.$route.query.onboarding) query.onboarding = this.$route.query.onboarding
      if (this.$route.query.categoryId) query.categoryId = this.$route.query.categoryId
      if (this.$route.query.taskId) query.taskId = this.$route.query.taskId

      // Add filter parameters
      if (this.filters.project.length > 0) query.project = this.filters.project.join(',')
      if (this.filters.category.length > 0) query.category = this.filters.category.join(',')
      if (this.filters.user.length > 0) query.user = this.filters.user.join(',')
      if (this.filters.date_range.from) query.date_range_from = this.filters.date_range.from
      if (this.filters.date_range.to) query.date_range_to = this.filters.date_range.to
      if (this.filters.running_status) query.running_status = this.filters.running_status

      this.$router.replace({ query }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },

    // Pagination
    handlePageChange() {
      this.getTimeLogs(this.perPage, this.getOffset())
    },

    getOffset() {
      return (this.currentPage * this.perPage) - this.perPage
    },

    // Utility methods
    openStartTrackerModal() {
      const tz = JSON.parse(localStorage.getItem('group')).tz_name
      const now = tz ? moment.tz(tz) : moment()
      this.newTracker = {
        project_id: null,
        ticket_id: null,
        subtask_id: null,
        category_id: null,
        useCustomTime: false,
        start_date: now.format('YYYY-MM-DD'),
        start_time_only: now.format('HH:mm:ss'),
        notes: ''
      };
      this.projectTickets = [];
      this.projectSubtasks = [];

      // Load projects if not already loaded
      if (this.projects.length === 0) {
        this.loadProjects();
      }

      this.modals.start_tracker = true;
    },

    openSwitchTaskModal() {
      this.isSwitchMode = true;
      const tz = JSON.parse(localStorage.getItem('group')).tz_name
      const now = tz ? moment.tz(tz) : moment()
      this.newTracker = {
        project_id: null,
        ticket_id: null,
        subtask_id: null,
        category_id: this.activeTimer ? this.activeTimer.category_id : null,
        useCustomTime: false,
        start_date: now.format('YYYY-MM-DD'),
        start_time_only: now.format('HH:mm:ss'),
        notes: ''
      };
      this.projectTickets = [];
      this.projectSubtasks = [];

      if (this.projects.length === 0) {
        this.loadProjects();
      }

      this.modals.start_tracker = true;
    },

    closeStartTrackerModal() {
      this.modals.start_tracker = false;
      this.isSwitchMode = false;
      this.newTracker = {
        project_id: null,
        ticket_id: null,
        subtask_id: null,
        category_id: null,
        useCustomTime: false,
        start_date: null,
        start_time_only: null,
        notes: ''
      };
      this.projectTickets = [];
      this.projectSubtasks = [];
      this.errs.category_id = '';
    },
    validateStartTracker() {
      // Reset errors
      this.errs.category_id = '';

      let isValid = true;

      // Validate category
      if (!this.newTracker.category_id) {
        this.errs.category_id = 'Please select a category';
        isValid = false;
      }

      return isValid;
    },
    async startTracker(extra = {}) {
      if (!this.validateStartTracker()) {
        return;
      }
      this.submittingTracker = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };

        let startTime;

        if (this.newTracker.useCustomTime && this.newTracker.start_date && this.newTracker.start_time_only) {
          const tz = JSON.parse(localStorage.getItem('group')).tz_name
          const local = `${this.newTracker.start_date} ${this.newTracker.start_time_only}`
          startTime = (tz ? moment.tz(local, tz) : moment(local)).utc().toISOString()
        } else {
          startTime = new Date().toISOString();
        }

        // custom_start tells the server the start time was picked by hand, so it is a
        // manual entry; a normal start is checked against location at the server's time.
        const customStart = !!(this.newTracker.useCustomTime && this.newTracker.start_date && this.newTracker.start_time_only)
        const data = {
          start_time: startTime,
          is_running: true,
          project_id: this.newTracker.project_id,
          ticket_id: this.newTracker.subtask_id || this.newTracker.ticket_id,
          category_id: this.newTracker.category_id,
          notes: this.newTracker.notes || null,
          custom_start: customStart,
          ...(customStart ? {} : await this.locationPayload()),
          ...extra
        };

        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs`;
        await axios.post(url, data, config);

        // Close modal and refresh the list
        this.closeStartTrackerModal();
        await this.getTimeLogs(this.perPage, this.getOffset(), true);

      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else if ((err.response?.status === 400 || err.response?.status === 403) && this.handleLocationCheckError(err)) {
          // Shown by handleLocationCheckError
        } else if (err.response?.status === 400) {
          this.showAlert('Error', err.response.data, 'danger');
        } else {
          this.showAlert('Error', 'Failed to start time tracker', 'danger');
        }
      } finally {
        this.submittingTracker = false;
      }
    },

    async switchTask() {
      if (!this.validateStartTracker()) {
        return;
      }

      if (!this.activeTimer || !this.activeTimer.id) {
        // Timer was stopped externally, fall back to normal start
        await this.startTracker();
        return;
      }

      this.submittingTracker = true;
      let stopSucceeded = false;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };

        // Step 1: Stop the current timer
        const stopUrl = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs/${this.activeTimer.id}/stop`;
        await axios.put(stopUrl, await this.locationPayload(), config);
        stopSucceeded = true;

        // Step 2: Start the new timer
        const data = {
          start_time: new Date().toISOString(),
          is_running: true,
          project_id: this.newTracker.project_id,
          ticket_id: this.newTracker.subtask_id || this.newTracker.ticket_id,
          category_id: this.newTracker.category_id,
          notes: this.newTracker.notes || null,
          custom_start: false,
          ...(await this.locationPayload())
        };

        const startUrl = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs`;
        await axios.post(startUrl, data, config);

        // Close modal and refresh
        this.closeStartTrackerModal();
        await this.getTimeLogs(this.perPage, this.getOffset(), true);

      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else if (stopSucceeded && this.handleLocationCheckError(err)) {
          // The previous timer is stopped; Continue in the warning dialog uses the normal start.
          await this.getTimeLogs(this.perPage, this.getOffset(), true);
        } else if (stopSucceeded) {
          // Stop worked but start failed
          this.showAlert('Partial switch', 'Your previous task was stopped, but the new timer could not be started. Please try starting it manually.', 'warning');
          await this.getTimeLogs(this.perPage, this.getOffset(), true);
        } else if (err.response?.status === 400) {
          this.showAlert('Error', err.response.data, 'danger');
        } else {
          this.showAlert('Error', 'Failed to switch task', 'danger');
        }
      } finally {
        this.submittingTracker = false;
      }
    },

    async loadProjects() {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects?count=100&offset=0`;

        const response = await axios.get(url, config);
        this.projects = response.data.projects || [];
      } catch (err) {
        console.error('Failed to load projects:', err);
      }
    },

    async loadSubtasksForTicket(projectId, ticketId) {
      if (!projectId || !ticketId) return []
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${projectId}/tickets?count=10000&offset=0&parent_id=${ticketId}`;
        const response = await axios.get(url, config);
        return response.data.tickets || [];
      } catch (err) {
        console.error('Failed to load subtasks:', err);
        return [];
      }
    },

    async onTicketChange(ticketId) {
      this.newTracker.subtask_id = null;
      this.projectSubtasks = [];
      if (ticketId && this.selectedTicketHasSubtasks) {
        this.projectSubtasks = await this.loadSubtasksForTicket(this.newTracker.project_id, ticketId);
      }
    },

    async onEditTicketChange(ticketId) {
      this.editTracker.subtask_id = null;
      this.editProjectSubtasks = [];
      if (ticketId && this.selectedEditTicketHasSubtasks) {
        this.editProjectSubtasks = await this.loadSubtasksForTicket(this.editTracker.project_id, ticketId);
      }
    },

    async onProjectChange(projectId) {
      // Clear ticket selection when project changes
      this.newTracker.ticket_id = null;
      this.newTracker.subtask_id = null;
      this.projectTickets = [];
      this.projectSubtasks = [];

      if (projectId) {
        // Load tickets for the selected project
        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${projectId}/tickets?count=10000&offset=0`;

          const response = await axios.get(url, config);
          this.projectTickets = response.data.tickets || [];
        } catch (err) {
          console.error('Failed to load tickets:', err);
        }
      }
    },

    async pauseTimer() {
      // Check if activeTimer exists and has an ID
      if (!this.activeTimer || !this.activeTimer.id) {
        this.showAlert('Error', 'No active timer found', 'danger');
        return;
      }

      // If timer is already paused, resume it
      if (this.activeTimer.is_paused) {
        await this.resumeTimer();
        return;
      }

      this.pauseActionLabel = 'Pausing...';
      this.togglingPause = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs/${this.activeTimer.id}/pause`;

        const response = await axios.put(url, {}, config);

        // Update the active timer with the response
        if (response.data && response.data.time_log) {
          this.activeTimer = response.data.time_log;
        }

        // Refresh the time logs
        await this.getTimeLogs(this.perPage, this.getOffset(), true);

      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else if (err.response?.status === 400) {
          this.showAlert('Error', err.response.data || 'Cannot pause timer', 'danger');
        } else {
          this.showAlert('Error', 'Failed to pause timer', 'danger');
        }
      } finally {
        this.togglingPause = false;
      }
    },

    async resumeTimer() {
      this.pauseActionLabel = 'Resuming...';
      this.togglingPause = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs/${this.activeTimer.id}/resume`;

        const response = await axios.put(url, {}, config);

        // Update the active timer with the response
        if (response.data && response.data.time_log) {
          this.activeTimer = response.data.time_log;
        }

        // Refresh the time logs
        await this.getTimeLogs(this.perPage, this.getOffset(), true);

      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else {
          this.showAlert('Error', 'Failed to resume timer', 'danger');
        }
      } finally {
        this.togglingPause = false;
      }
    },

    async stopTimer() {
      if (!this.activeTimer || !this.activeTimer.id) {
        this.showAlert('Error', 'No active timer found', 'danger');
        return;
      }

      this.stoppingTimer = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs/${this.activeTimer.id}/stop`;

        await axios.put(url, await this.locationPayload(), config);

        // Clear the active timer and refresh
        this.activeTimer = null;
        await this.getTimeLogs(this.perPage, this.getOffset(), true);

      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else {
          this.showAlert('Error', 'Failed to stop timer', 'danger');
        }
      } finally {
        this.stoppingTimer = false;
      }
    },

    configureCategories(){
      this.modals.configure_categories = true
      // Categories are already loaded from getTimeLogs
    },

    closeCategoriesDrawer(){
      this.modals.configure_categories = false
      this.edit_categories = false
    },

    addNewCategory(){
      this.selectedCategory = null
      this.categoryErrors = {}
      this.edit_categories = true
    },

    editCategory(item){
      this.selectedCategory = {...item}
      this.categoryErrors = {}
      this.edit_categories = true
    },

    // Name, color, description and location check save together (OTC-2926).
    async saveCategory(payload){
      const creating = !this.selectedCategory
      this.categoryErrors = {}
      this.savingCategory = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const base = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-log-categories`
        const response = creating
          ? await axios.post(base, payload, config)
          : await axios.put(`${base}/${this.selectedCategory.id}`, payload, config)
        this.edit_categories = false
        this.categories = response.data.categories
        // Refresh the time logs quietly so category names and colors update
        this.getTimeLogs(this.perPage, this.getOffset(), true)
        this.notifyCategory('success', creating ? 'Category created.' : 'Category saved.')
      } catch(err) {
        const status = err.response?.status
        if(status === 401){
          this.$router.push('/forbidden')
        } else if(status === 400){
          // Location errors come back as JSON validation_errors; the older
          // checks (duplicate name, length) as a plain string.
          const data = err.response.data
          let body = data
          if (typeof data === 'string') {
            try { body = JSON.parse(data) } catch (e) { body = data }
          }
          this.categoryErrors = body && typeof body === 'object' && body.validation_errors
            ? body.validation_errors
            : { name: typeof body === 'string' ? body : 'Check the category details and try again.' }
        } else {
          this.notifyCategory('danger', creating ? "The category couldn't be created. Try again." : "The category couldn't be saved. Try again.")
        }
      } finally {
        this.savingCategory = false
      }
    },

    async archiveCategory(item){
      const ok = await this.askConfirm('Archive this category?', `Archive category "${item.name}"?`, 'Archive')
      if (!ok) return

      this.savingCategory = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-log-categories/${item.id}`

        const response = await axios.delete(url, config)
        this.categories = response.data.categories
        this.edit_categories = false
        // Refresh the time logs quietly so category names and colors update
        this.getTimeLogs(this.perPage, this.getOffset(), true)
        this.notifyCategory('success', 'Category archived.')
      } catch(err) {
        if(err.response?.status === 401){
          this.$router.push('/forbidden')
        } else {
          this.notifyCategory('danger', "The category couldn't be archived. Try again.")
        }
      } finally {
        this.savingCategory = false
      }
    },

    getLocalDateTime(date) {
      if(!date) return ''
      const d = formatGroupDate(date, { dayOfWeek: false })
      const t = formatGroupTime(date)
      if(!d && !t) return ''
      return `${d}, ${t}`
    },

    formatMinutesToTimeString(totalMinutes) {
      if (!totalMinutes) return '0m';

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      const parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

      return parts.join(' ');
    },

    formatElapsedTime(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    },

    formatElapsedTimeWithColon(seconds) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },


    calculateRunningDurationInSeconds(startTime) {
      if (!this.activeTimer) {
        const start = moment(startTime);
        const now = moment();
        return Math.floor(now.diff(start) / 1000);
      }

      const start = moment(startTime);
      const now = moment();
      let totalSeconds = Math.floor(now.diff(start) / 1000);

      // Subtract any previously accumulated paused time
      if (this.activeTimer.paused_duration_minutes) {
        totalSeconds -= (this.activeTimer.paused_duration_minutes * 60);
      }

      // If currently paused, also subtract the current pause period
      if (this.activeTimer.is_paused && this.activeTimer.last_pause_time) {
        const pauseTime = moment(this.activeTimer.last_pause_time);
        const currentPauseDuration = Math.floor(now.diff(pauseTime) / 1000);
        totalSeconds -= currentPauseDuration;
      }

      return Math.max(0, totalSeconds);
    },

    calculateActiveMinutes(timeLog) {
      const start = moment(timeLog.start_time);
      const now = moment();
      let totalMinutes = Math.floor(now.diff(start) / 60000);

      // If the timer has paused duration, subtract it
      if (timeLog.paused_duration_minutes) {
        totalMinutes -= timeLog.paused_duration_minutes;
      }

      // If currently paused, also subtract the current pause period
      if (timeLog.is_paused && timeLog.last_pause_time) {
        const pauseTime = moment(timeLog.last_pause_time);
        const currentPauseDuration = Math.floor(now.diff(pauseTime) / 60000);
        totalMinutes -= currentPauseDuration;
      }

      return Math.max(0, totalMinutes);
    },

    startTimerUpdate() {
      // Update running timers every second
      this.timerInterval = setInterval(() => {
        this.$forceUpdate(); // Force re-render to update running timers
      }, 1000);
    },

    stopTimerUpdate() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },

    async getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },

    async getTimeLogs(count, offset, silent = false) {
      if (!silent) this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };

        // Build query parameters
        const params = this.buildQueryParams();
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs`;
        url += `?${params}`;

        const response = await axios.get(url, config);

        this.timeLogs = response.data.time_logs || [];
        this.totalRows = response.data.total_rows || 0;
        this.analytics = response.data.analytics || null;
        this.activeTimer = response.data.active_timer || null;
        this.categories = response.data.categories || [];
        this.venuePinSet = !!response.data.venue_pin_set;
        this.locationChecksEnabled = !!response.data.location_checks_enabled;
        this.offSiteAutomationActive = !!response.data.off_site_automation_active;
        this.projects = response.data.projects || [];
        this.users = response.data.users || [];

        // Update URL with current filters
        this.updateUrlQuery();

        // Check if there are any running timers and start updates
        const hasRunningTimer = this.timeLogs.some(log => log.is_running);
        if (hasRunningTimer || this.activeTimer) {
          this.startTimerUpdate();
        } else {
          this.stopTimerUpdate();
        }

      } catch (err) {
        this.loading = false;
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else {
          this.showAlert('Error', 'Failed to load time logs', 'danger');
        }
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>

.running-pulse {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: #28a745;
  border-radius: 50%;
  margin-right: 5px;
  animation: pulse 1.5s infinite;
}

.active-timer-card{
  text-align: center;
  padding: 45px 0 30px;
  border: 1.5px solid #e14eca;
  border-radius: .1875rem;
  margin: 15px 0;
  min-height: 168px;
}

.active-timer-content {
  max-width: 600px;
  margin: 0 auto;
}

.active-timer-display {
  font-size: 4rem;
  font-weight: 300;
  color: white;
  margin-bottom: 15px;
  letter-spacing: 0.1em;
  font-family: 'Courier New', monospace;
}

.paused-indicator {
  font-size: 2.5rem;
  margin-right: 15px;
  vertical-align: middle;
  animation: blink 1.5s infinite;
}

.paused-text {
  color: #e14eca;
  font-weight: bold;
  animation: blink 1.5s infinite;
}

@keyframes blink {
  0%, 50%, 100% { opacity: 1; }
  25%, 75% { opacity: 0.5; }
}

.active-timer-subtitle {
  opacity: 0.9;
  font-size: 1rem;
}

.active-timer-project {
  font-size: 1.1rem;
  opacity: 0.95;
}

.active-timer-category {
  font-size: 1rem;
  opacity: 0.95;
}

.active-timer-notes {
  font-size: 0.95rem;
  opacity: 0.9;
  font-style: italic;
}

.active-timer-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 30px;
}

.active-timer-actions button {
  min-width: 120px;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.font-icon-detail {
  text-align: center;
  padding: 2rem;
}

.btn-simple {
  transition: all 0.3s ease;
}

.btn-simple:hover {
  transform: translateY(-1px);
}


@media (max-width: 768px) {

  .active-timer-card {
    padding: 30px 15px;
  }

  .active-timer-display {
    font-size: 2.5rem;
  }

  .active-timer-subtitle {
    font-size: 0.9rem;
  }

  .active-timer-project {
    font-size: 1rem;
  }

  .active-timer-actions {
    flex-direction: row;
    gap: 10px;
  }

  .active-timer-actions button {
    min-width: 100px;
    font-size: 0.9rem;
  }
}

.switch-context-banner {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-left: 3px solid #e14eca;
  border-radius: 4px;
  padding: 12px 16px;
}

.switch-elapsed-time {
  font-size: 1.1em;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #e14eca;
}

</style>
